<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <h2>IRR Settings - {{ existingData.name }}</h2>
    <p><small>IRR ID # {{ existingData.id }}</small><br>&nbsp;</p>
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <form id="irrForm" method="POST" enctype="multipart/form-data" :action="'/irr/' + existingData.id + '/'">
            <input type="hidden" name="_token" :value="csrf_token">
            <input v-if="route == 'edit'" type="hidden" name="id" :value="existingData.id"> <!-- include the IRR id in a hidden input, so that it will be returned if back-end validation fails -->
            <input v-if="route == 'edit'" type="hidden" name="_method" value="PUT"> <!-- HTML forms can't make PUT or PATCH requests - add a hidden _method field to spoof these HTTP verbs -->
            <ul class="alert alert-danger errorMessages" role="alert"></ul>
            <p>Fields marked with an asterisk * are required</p>
            <div class="form-group"><label for="latitude"><strong>* Area of Coverage</strong></label>
              <input type="hidden" id="latitude" name="latitude" v-model="formData.latitude">
              <input type="hidden" id="longitude" name="longitude" v-model="formData.longitude">
            </div>
            <div id="irrmap" style="height: 400px"></div>
            <div class="mt-3">
              <label for="map_address_search" class="font-weight-bold">Find address on map</label>
              <small class="form-text text-muted">Enter an address and click Find Address to move and zoom the map near that location.</small>
              <div class="map-address-search-row">
                <input type="text" class="form-control" id="map_address_search" v-model.trim="mapAddressSearch" @input="handleAddressInput" @keyup.enter.prevent="handleAddressSearch" placeholder="e.g. 5000 Forbes Avenue, Pittsburgh PA">
                <button class="btn btn-primary mt-0 map-find-address-button" type="button" @click.stop.prevent="handleAddressSearch" :disabled="mapSearchLoading">{{ mapSearchLoading ? 'Searching...' : 'Find Address' }}</button>
              </div>
              <ul v-if="mapAddressSuggestions.length" class="list-group map-address-suggestions">
                <li v-for="(suggestion, index) in mapAddressSuggestions" :key="index" class="list-group-item list-group-item-action" @mousedown.stop.prevent="selectAddressSuggestion(suggestion)">{{ suggestion.label }}</li>
              </ul>
              <small v-if="mapSearchError" class="form-text text-danger">{{ mapSearchError }}</small>
            </div>
            <div class="alert alert-primary map-location-help-banner" role="alert"><center><h5>Click anywhere on the map to change your Registry's location.  Adjust its area of coverage using the Radius setting below.</h5><center></center></center></div>
            <div class="form-group"><label for="radius"><strong>* Radius (meters)</strong></label>
              <input type="number" name="radius" class="form-control" id="radius" v-model="formData.radius" min="0" placeholder="" required="">
            </div>
            <div class="form-group"><label for="name"><strong>* Name</strong></label>
              <input type="text" name="name" class="form-control" id="name" v-model="formData.name" placeholder="" required="">
            </div>
            <div class="form-group"><label for="description"><strong>* Description</strong></label>
              <textarea name="description" class="form-control" id="description" rows="3" v-model="formData.description" required=""></textarea>
            </div>
            <div class="form-group"><label for="contact_name"><strong>* Contact Name</strong></label>
              <input type="text" name="contact_name" class="form-control" id="contact_name" v-model="formData.contact_name" placeholder="" required="">
            </div>
            <div class="form-group"><label for="contact_email"><strong>* Contact Email</strong></label>
              <input type="email" name="contact_email" class="form-control" id="contact_email" v-model="formData.contact_email" placeholder="" required="">
            </div>
            <div class="form-group"><label for="url"><strong>URL</strong></label>
              <input type="url" name="url" class="form-control" id="url" v-model="formData.url" placeholder="">
            </div>
            <!-- Hidden field - always set to "Anyone can request to publish IoT resources" -->
            <input type="hidden" name="contributor_status_required" value="0">
            <button type="submit" class="btn btn-success">Save Changes</button>
            <a href="#" onclick="event.preventDefault(); window.history.back();" class="btn btn-danger">Cancel</a>
          </form>
        </div>
        <div class="col-md-4">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        route: String,
        csrf_token: String,
        errors: Array,
        existingData: [Object, Array],
      },
      data: function () {
        return {
          map: false,
          mapMarker: false,
          defaultMapZoom: 15,
          formData: {
            latitude: (this.existingData && this.existingData.latitude) ? this.existingData.latitude : 500,
            longitude: (this.existingData && this.existingData.longitude) ? this.existingData.longitude : 500,
            radius: (this.existingData && this.existingData.radius) ? this.existingData.radius : '',
            name: (this.existingData && this.existingData.name) ? this.existingData.name : '',
            description: (this.existingData && this.existingData.description) ? this.existingData.description : '',
            contact_name: (this.existingData && this.existingData.contact_name) ? this.existingData.contact_name : '',
            contact_email: (this.existingData && this.existingData.contact_email) ? this.existingData.contact_email : '',
            url: (this.existingData && this.existingData.url) ? this.existingData.url : '',
          },
          mapAddressSearch: '',
          mapAddressSuggestions: [],
          mapSearchError: '',
          mapSearchLoading: false,
          mapSearchDebounceTimer: false,
        }
      },
      created() {
        //decode options JSON and add values to existingData
        if(this.route == 'edit') {
          this.existingData.options = JSON.parse(this.existingData.options);
          this.existingData.contributor_status_required = this.existingData.options.contributor_status_required;
        }
      },
      beforeDestroy() {
        if(this.mapSearchDebounceTimer) {
          clearTimeout(this.mapSearchDebounceTimer);
        }
      },
      mounted() {
        this.$nextTick(function () {  //don't run until the entire view has been rendered
          $('form').each(this.createAllErrors);
          this.initMap();

          //display validation errors sent from server
          this.showServerErrors();
        })
      },
      methods: {
        handleAddressInput: function() {
          this.mapSearchError = '';

          if(this.mapSearchDebounceTimer) {
            clearTimeout(this.mapSearchDebounceTimer);
          }

          if(!this.mapAddressSearch || this.mapAddressSearch.length < 3) {
            this.mapAddressSuggestions = [];
            return;
          }

          this.mapSearchDebounceTimer = setTimeout(async () => {
            var suggestions = await this.fetchAddressSuggestions(this.mapAddressSearch, 5);
            this.mapAddressSuggestions = suggestions;
          }, 300);
        },
        fetchAddressSuggestions: async function(query, limit) {
          if(!query) {
            return [];
          }

          try {
            var encodedAddress = encodeURIComponent(query);
            var response = await fetch('https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=' + limit + '&q=' + encodedAddress, {
              headers: {
                'Accept': 'application/json'
              }
            });

            if(!response.ok) {
              return [];
            }

            var geocodeResults = await response.json();
            if(!Array.isArray(geocodeResults)) {
              return [];
            }

            return geocodeResults.map(function(result) {
              return {
                label: result.display_name,
                lat: parseFloat(result.lat),
                lng: parseFloat(result.lon),
              };
            }).filter(function(result) {
              return !isNaN(result.lat) && !isNaN(result.lng);
            });
          } catch (error) {
            return [];
          }
        },
        selectAddressSuggestion: function(suggestion) {
          this.mapAddressSearch = suggestion.label;
          this.mapAddressSuggestions = [];
          this.moveMapToCoordinates(suggestion.lat, suggestion.lng);
        },
        moveMapToCoordinates: function(lat, lng) {
          this.$data.map.setView([lat, lng], Math.max(this.$data.defaultMapZoom, 16));
          this.handleMapClick({latlng: {lat: lat, lng: lng}});
        },
        handleAddressSearch: async function() {
          this.mapSearchError = '';
          this.mapAddressSuggestions = [];

          if(!this.mapAddressSearch) {
            this.mapSearchError = 'Please enter an address to search.';
            return;
          }

          this.mapSearchLoading = true;

          try {
            var geocodeResults = await this.fetchAddressSuggestions(this.mapAddressSearch, 5);

            if(!Array.isArray(geocodeResults) || !geocodeResults.length) {
              this.mapSearchError = 'Address not found. Please try a more specific address.';
              return;
            }

            this.mapAddressSuggestions = geocodeResults;
            var topResult = geocodeResults[0];

            this.moveMapToCoordinates(topResult.lat, topResult.lng);
          } catch (error) {
            this.mapSearchError = 'Unable to search for that address right now. Please try again.';
          } finally {
            this.mapSearchLoading = false;
          }
        },
        initMap: function() {
          //init the map
          this.$data.map = window.L.map('irrmap').setView([39.8, -98.6], 4); //default to United States view

          //init some map components
          this.$data.mapMarker = L.marker([39.8, -98.6]);
      		this.$data.mapCircle = L.circle([39.8, -98.6], {
      			color: 'orange',
      			fillColor: '#ffa200',
      			fillOpacity: 0.35,
            radius: this.formData.radius
      		});

          //add the content
          //L.tileLayer('/map/tile/{s}/{id}/{z}/{x}/{y}', {  //for use when proxying the tile requests

          /* DEPRECATED L.tileLayer('https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}', {
      			attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/" target="_blank">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/" target="_blank">CC-BY-SA</a>',
      			maxZoom: 18,
            minZoom: 3,
      			id: 'mapbox.streets',
      			accessToken: 'pk.eyJ1IjoiamRvbm5lbGwiLCJhIjoiY2ptZHN1azhqMHR2MDNxbnF2eWloOTRoeSJ9.RlFLdSY8e_anTpJNUJhIkA'
      		}).addTo(this.$data.map); */

          L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
            attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
            tileSize: 512,
            maxZoom: 18,
            minZoom: 3,
            zoomOffset: -1,
            id: 'jdonnell/ckg9vp1o403lx1as8i81og3ir',
            accessToken: 'pk.eyJ1IjoiamRvbm5lbGwiLCJhIjoiY2ptZHN1azhqMHR2MDNxbnF2eWloOTRoeSJ9.RlFLdSY8e_anTpJNUJhIkA'
          }).addTo(this.$data.map);

          window.L.control.scale().addTo(this.map);

          /* check if existing data has valid LatLng set */
          var validLatLng = true;
          //latitude check
          if('latitude' in this.existingData) {  //check that value exists
            if(!this.existingData.latitude || !(this.existingData.latitude <= 90 && this.existingData.latitude >= -90)) {  //check valid range
              validLatLng = false;
            }
          } else { validLatLng = false; }
          //longitude check
          if('longitude' in this.existingData) {  //check that value exists
            if(!this.existingData.longitude || !(this.existingData.longitude <= 180 && this.existingData.longitude >= -180)) {  //check valid range
              validLatLng = false;
            }
          } else { validLatLng = false; }

          //if valid LatLng, move the map to that location and place a marker
          if(validLatLng) {
            this.$data.map.setView([this.existingData.latitude, this.existingData.longitude], this.$data.defaultMapZoom);
            var fakeEvent = {latlng: {lat: this.existingData.latitude, lng: this.existingData.longitude}};
            this.handleMapClick(fakeEvent);
          }
          //else attempt to get LatLng based on ip address, then move map to that location
          else {
            //attempt to get LatLng based on ip address, then move map to that location
            var self = this;
            //geolocation via browser
            if(navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(self.setMapLatLng);
            } else { //Geolocation is not supported by this browser
              this.$data.mapPopup
                .setLatLng([$('#latitude').val(), $('#longitude').val()])
                .setContent('<p>Geolocation is not supported by this browser</p>')
                .openOn(this.$data.map);
            }
          }

          //handle map clicks
          this.$data.map.on('click', this.handleMapClick);

          //add onChange function to radius form element
          $('#radius').change(this.handleRadiusChange);
        },
        createRadioElement: function(name, value) {
          var checked = (this.existingData.contributor_status_required == value) ? 'checked=""' : '';
          return'<input class="form-check-input" type="radio" name="' + name + '" value="' + value + '" id="' + value + '"' + checked + '>';
        },
        handleMapClick: function(e) {
          /* fix for leaflet
           * leaflet allows the map to scroll left/right indefinitely, resulting in longitiude values that are outside the -180 to 180 range
           * if a user clicks in one of these areas, the longitude is adjusted accordingly
           */
          while(e.latlng.lng > 180) {
            e.latlng.lng -= 360;
          }
          while(e.latlng.lng < -180) {
            e.latlng.lng += 360;
          }

          this.$data.map.setView([e.latlng.lat,e.latlng.lng]);  //center the map on the location clicked
          this.$data.mapMarker.setLatLng(e.latlng).addTo(this.$data.map);  //place a marker
          this.$data.mapCircle.setLatLng(e.latlng).setRadius(this.formData.radius).addTo(this.$data.map); //place the area of coverage circle
          this.formData.latitude = e.latlng.lat;  //set hidden latitude form field
          this.formData.longitude = e.latlng.lng;  //set hidden longitude form field
        },
        handleRadiusChange: function() {
          this.$data.mapCircle.setRadius(this.formData.radius)
        },
        setMapLatLng: function(position) {
          if (typeof position.coords.latitude !== 'undefined' && typeof position.coords.longitude !== 'undefined') {
            this.$data.map.setView([position.coords.latitude, position.coords.longitude], this.$data.defaultMapZoom);
          }
        },
        showServerErrors: function() {
          //if validation errors were returned from the server
          if(this.errors.length) {
            var form = $('form');
            var errorList = $('ul.errorMessages', form);
            errorList.empty();
            var errorHTML = '';
            this.errors.forEach((error, i) => {
                  errorHTML += '<li>' + error + '</li>';
                }
            );
            errorList.show().append(errorHTML);
          }
        },
        createAllErrors: function() {
          var form = $('form');
          $('input[type=submit], button[type=submit]', form).on('click', this.showAllErrorMessages);
          $('input[type=text]', form).on('keypress', function(event) {
            if (this.id == 'map_address_search') {
              return;
            }
            //keyCode 13 = Enter
            if (event.keyCode == 13) {
              this.showAllErrorMessages();
            }
          });
        },
        showAllErrorMessages: function() {
            var form = $('form');
            var errorList = $('ul.errorMessages', form);
            errorList.empty();

            //Find all invalid fields within the form.
            var firstErrorTabSelected = false; //the tab containing the field with the first error should be automatically selected
            form.find(':invalid').each(function(index, node) {
              //Find the field's corresponding label
              var label = $('label[for=' + node.id + ']');

              //check if the tab with the first error has been selected yet
              if(!firstErrorTabSelected) {
                var tabId = $(node).parent().parent()[0].id + '-tab';  //get the tab id
                $('#' + tabId).click();  //call it's click method
                firstErrorTabSelected = true;
              }

              //Opera incorrectly does not fill the validationMessage property.
              var message = node.validationMessage || 'Invalid value.';
              errorList
                .show()
                .append('<li><span>' + label.html() + '</span>&nbsp;&nbsp;&nbsp;' + message + '</li>');
            });
          },
      } //close methods
    } //close export
</script>
<style>
  .errorMessages {
      display: none;
      list-style-type: none;
  }
  .errorMessages span {
      font-weight: bold;
  }
  .prev-button {
    margin-right: 10px;
  }
  .btn {
    margin-top: 25px;
  }
  .map-address-suggestions {
    max-height: 220px;
    overflow-y: auto;
  }
  .map-address-search-row {
    display: flex;
    align-items: stretch;
    gap: 12px;
  }
  .map-address-search-row .form-control {
    flex: 1 1 auto;
  }
  .map-find-address-button {
    white-space: nowrap;
    min-width: 160px;
  }
  .map-location-help-banner {
    margin-top: 16px;
  }
</style>
